import { useEffect, useLayoutEffect } from 'react';

const DEBOUNCE_MS = 150;
const RESTORE_TIMEOUT_MS = 2000;

const safeGet = (key) => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
};

/**
 * Preserva la posición de scroll de un container entre navegaciones SPA.
 *
 * Útil para containers con su PROPIO scroll (overflow-y: auto) — el browser
 * no maneja restoration en estos casos automáticamente (solo en window scroll
 * de páginas reales).
 *
 * Comportamiento:
 *  - Al montar: lee la última posición guardada en sessionStorage para `key`
 *    y la aplica. Si el contenido aún no creció lo suficiente (lazy images,
 *    data async), reintenta cada frame + escucha ResizeObserver hasta achieve
 *    target o timeout 2s.
 *  - Durante uso: escucha scroll del container y guarda posición (debounced 150ms).
 *  - Al desmontar: guarda última posición conocida SI el usuario realmente scroleó.
 *
 * GOTCHAS CRÍTICOS resueltos acá:
 *
 * 1. **StrictMode fake-unmount**: en dev, React monta → desmonta inmediato →
 *    remonta para detectar side effects mal limpiados. Sin protección, ese
 *    "fake cleanup" pisaría sessionStorage con scrollTop=0 (acaba de montar,
 *    sin scroll aún). Fix: flag `hasUserScrolled` — solo guardar si hubo
 *    interacción real.
 *
 * 2. **Detached element scrollTop=0**: cuando React desmonta el componente,
 *    el DOM se remueve ANTES que corra el cleanup. Leer `el.scrollTop` de un
 *    elemento detached devuelve 0, pisando el valor real. Fix: trackear
 *    `lastKnownScrollTop` en cada scroll event — usar esa var en cleanup
 *    en vez de leer del elemento (que ya está detached).
 *
 * @param {React.RefObject} ref - ref al elemento con overflow-y
 * @param {string} key - clave de sessionStorage (única por path/sección)
 * @param {boolean} enabled - default true. Útil para gating de mounts diferidos.
 */
export function useScrollRestoration(ref, key, enabled = true) {
  // RESTORE — al mount + retry con rAF/ResizeObserver hasta que el contenido
  // tenga altura suficiente para acomodar el target scrollTop.
  useLayoutEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const saved = safeGet(key);
    if (saved === null) return;
    const target = parseInt(saved, 10) || 0;
    if (target === 0) return;

    el.scrollTop = target;
    if (el.scrollTop === target) return;

    let rafId = null;
    let ro = null;
    const startTime = performance.now();

    const tryRestore = () => {
      if (!ref.current) return;
      ref.current.scrollTop = target;
      const achieved = ref.current.scrollTop === target;
      const timedOut = performance.now() - startTime > RESTORE_TIMEOUT_MS;
      if (achieved || timedOut) {
        ro?.disconnect();
        ro = null;
        return;
      }
      rafId = requestAnimationFrame(tryRestore);
    };

    ro = new ResizeObserver(() => {
      if (!ref.current) return;
      if (ref.current.scrollTop !== target) {
        ref.current.scrollTop = target;
        if (ref.current.scrollTop === target) {
          ro?.disconnect();
          ro = null;
        }
      }
    });
    ro.observe(el);

    rafId = requestAnimationFrame(tryRestore);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro?.disconnect();
    };
  }, [enabled, key, ref]);

  // SAVE — listener debounced + final en cleanup desde lastKnownScrollTop.
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    let hasUserScrolled = false;
    let lastKnownScrollTop = el.scrollTop;
    let timer;

    const handler = () => {
      hasUserScrolled = true;
      lastKnownScrollTop = el.scrollTop;
      clearTimeout(timer);
      timer = setTimeout(() => {
        safeSet(key, String(lastKnownScrollTop));
      }, DEBOUNCE_MS);
    };

    el.addEventListener('scroll', handler, { passive: true });
    return () => {
      el.removeEventListener('scroll', handler);
      clearTimeout(timer);
      // Solo guardar si el usuario interactuó (evita que StrictMode fake-unmount
      // pise el valor) y usar lastKnownScrollTop en vez de el.scrollTop —
      // el elemento ya puede estar detached cuando cleanup corre, y leer
      // scrollTop de un detached element devuelve 0.
      if (hasUserScrolled) {
        safeSet(key, String(lastKnownScrollTop));
      }
    };
  }, [enabled, key, ref]);
}
